import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * HeatJeopardy — Heat & Thermodynamics (Lessons 21–23) review game for King's Academy.
 * Self-contained: no external dependencies, all styles namespaced under `.jpd`.
 * Physical buzzers in the room; the teacher judges each answer with the
 * Correct / Wrong buttons. These are reversible toggles that stay live before
 * and after the answer is revealed: click again to undo, or switch a team from
 * wrong to correct and the score self-corrects. Two random Daily Doubles double
 * the stakes.
 *
 * Deliberately light on math: one easy Kelvin conversion is the only calculation.
 * The emphasis is on concepts and on what each symbol in a formula means
 * (see the "Decode the Formula" category).
 *
 * Drop-in: default export, no required props. Renders anywhere in the portal.
 */

const CATEGORIES = [
  {
    name: "Kinetic Theory",
    clues: [
      { v: 100, q: "The kinetic theory says all matter is made of tiny particles that are constantly doing this.", a: "What is moving (in constant motion)?" },
      { v: 200, q: "Temperature is a measure of the average amount of THIS kind of energy of the particles in a substance.", a: "What is kinetic energy?" },
      { v: 300, q: "When you add heat to a substance, its particles do these two things.", a: "What is move faster and spread farther apart?" },
      { v: 400, q: "Solid, liquid, or gas: in which state do the particles have the most kinetic energy and move the fastest?", a: "What is a gas?" },
      { v: 500, q: "True or false: 'A big bucket of warm water has a higher temperature than a small cup of boiling water.'", a: "What is false? Temperature is the average kinetic energy PER particle, not total energy — the boiling water is hotter even though it holds less total thermal energy." },
    ],
  },
  {
    name: "Temperature Scales",
    clues: [
      { v: 100, q: "On the Celsius scale, water freezes at this temperature.", a: "What is 0°C?" },
      { v: 200, q: "The name for the coldest possible temperature, where particle motion essentially stops.", a: "What is absolute zero?" },
      { v: 300, q: "Absolute zero is this value on the Kelvin scale.", a: "What is 0 K (zero kelvin)?" },
      { v: 400, q: "Convert 25°C to kelvin.", a: "What is 298 K?" },
      { v: 500, q: "Explain why a temperature can never drop below 0 K.", a: "What is because particles can't have less than zero motion — you can't move slower than stopped, so there's no colder temperature?" },
    ],
  },
  {
    name: "Heat on the Move",
    clues: [
      { v: 100, q: "Heat always flows from ___ objects to ___ objects.", a: "What is from hot (warmer) to cold (cooler)?" },
      { v: 200, q: "When two objects reach the same temperature and heat stops flowing between them, they have reached this.", a: "What is thermal equilibrium?" },
      { v: 300, q: "Fill in the definition: heat is the ___ of thermal energy from one object to another.", a: "What is the transfer?" },
      { v: 400, q: "The First Law of Thermodynamics says energy cannot be created or destroyed — only this.", a: "What is transferred or transformed (energy is conserved)?" },
      { v: 500, q: "An 80°C block touches a 20°C block. Describe what happens to each one's temperature until they reach equilibrium.", a: "What is the hot block cools down and the cold block warms up (heat flows hot→cold) until both settle at the same temperature in the middle?" },
    ],
  },
  {
    name: "Decode the Formula",
    note: "Q = m · c · ΔT",
    clues: [
      { v: 100, q: "In Q = m · c · ΔT, the letter Q stands for this (measured in joules).", a: "What is heat — the thermal energy transferred?" },
      { v: 200, q: "In the heat equation, the letter m stands for this.", a: "What is mass?" },
      { v: 300, q: "What does the symbol ΔT ('delta T') mean?", a: "What is the change in temperature — final temperature minus starting temperature?" },
      { v: 400, q: "In the heat equation, the letter c stands for this property of the material.", a: "What is specific heat capacity?" },
      { v: 500, q: "The triangle symbol Δ (delta) shows up all over science. In general, what does it tell you to do?", a: "What is find the change — the difference between the final value and the starting value?" },
    ],
  },
  {
    name: "Specific Heat IRL",
    clues: [
      { v: 100, q: "Specific heat capacity tells you how much of THIS it takes to raise a substance's temperature.", a: "What is heat (energy)?" },
      { v: 200, q: "This everyday substance has an unusually HIGH specific heat, so it's slow to heat up and slow to cool down.", a: "What is water?" },
      { v: 300, q: "Using the 'thermal inertia' analogy: a substance with a high specific heat resists this.", a: "What is a change in temperature?" },
      { v: 400, q: "Why does a town on the coast stay milder than a town in the desert?", a: "What is because ocean water has a high specific heat — it heats and cools slowly and moderates the coast, while desert sand has a low specific heat and swings hot and cold fast?" },
      { v: 500, q: "Equal masses of metal and water get the SAME heat. The metal's temperature shoots up; the water's barely moves. Which has the higher specific heat, and why?", a: "What is water? A higher specific heat means more energy is needed per degree, so the same heat raises its temperature far less." },
    ],
  },
];

const COLS = CATEGORIES.length;
const ROWS = 5;
const DELTA = { correct: 1, wrong: -1 };

const CSS = `
.jpd{
  --navy-deep:#060f2e; --navy:#0b1e50; --navy-tile:#0d2360; --navy-tile-hover:#123086;
  --gold:#f2c14e; --gold-bright:#ffd766; --cream:#f7f3e8; --cream-dim:#d9d3c2;
  --used:#0a1636; --terracotta:#cf6a4c; --terracotta-bright:#e07d5e;
  --green:#4c9a6a; --green-bright:#5fb37f;
  --heading:"Cambria","Georgia","Times New Roman",serif;
  --body:"Calibri","Segoe UI",system-ui,-apple-system,sans-serif;
  font-family:var(--body); color:var(--cream); background:var(--navy-deep);
  min-height:100%; width:100%; position:relative;
}
.jpd *{box-sizing:border-box;margin:0;padding:0;}
.jpd button{font-family:var(--body);cursor:pointer;border:none;}
.jpd button:focus-visible{outline:3px solid var(--gold-bright);outline-offset:2px;}

.jpd .setup{min-height:100vh;display:flex;flex-direction:column;align-items:center;
  justify-content:center;text-align:center;padding:24px;gap:26px;}
.jpd .logo{font-family:var(--heading);color:var(--gold);text-shadow:4px 4px 0 rgba(0,0,0,.45);line-height:.95;}
.jpd .logo .big{display:block;font-size:clamp(3rem,11vw,7rem);font-weight:700;letter-spacing:.02em;}
.jpd .logo .sub{display:block;font-size:clamp(.9rem,3vw,1.5rem);color:var(--cream);margin-top:.5em;letter-spacing:.25em;}
.jpd .rule{width:min(520px,80vw);height:4px;background:var(--gold);border-radius:2px;}
.jpd .panel{background:var(--navy);border:2px solid var(--gold);border-radius:14px;padding:26px 28px;
  width:min(560px,92vw);display:flex;flex-direction:column;gap:18px;}
.jpd .panel h2{font-family:var(--heading);color:var(--gold-bright);font-size:1.4rem;font-weight:700;}
.jpd .count{display:flex;gap:10px;justify-content:center;}
.jpd .count button{background:var(--navy-tile);color:var(--cream);border:2px solid transparent;
  font-size:1.4rem;font-weight:700;width:56px;height:56px;border-radius:10px;transition:background .12s,border-color .12s;}
.jpd .count button:hover{background:var(--navy-tile-hover);}
.jpd .count button.active{border-color:var(--gold);color:var(--gold-bright);background:var(--navy-tile-hover);}
.jpd .names{display:flex;flex-direction:column;gap:10px;}
.jpd .names input{font-size:1.1rem;padding:11px 14px;border-radius:8px;border:2px solid var(--navy-tile-hover);
  background:var(--navy-deep);color:var(--cream);font-family:var(--body);}
.jpd .names input:focus{outline:none;border-color:var(--gold);}
.jpd .start{background:var(--gold);color:var(--navy-deep);font-size:1.4rem;font-weight:700;padding:15px;
  border-radius:10px;font-family:var(--heading);transition:background .12s,transform .08s;}
.jpd .start:hover{background:var(--gold-bright);}
.jpd .start:active{transform:scale(.98);}
.jpd .hint{color:var(--cream-dim);font-size:.92rem;line-height:1.5;max-width:560px;}
.jpd .hint strong{color:var(--cream);}

.jpd .game{min-height:100vh;display:flex;flex-direction:column;}
.jpd .topbar{display:flex;align-items:center;justify-content:space-between;padding:10px 18px;
  background:var(--navy);border-bottom:2px solid var(--gold);gap:12px;}
.jpd .topbar h1{font-family:var(--heading);color:var(--gold);font-size:1.25rem;font-weight:700;}
.jpd .tools{display:flex;gap:8px;}
.jpd .tool{background:var(--navy-tile);color:var(--cream);font-size:.9rem;font-weight:600;padding:9px 14px;
  border-radius:8px;transition:background .12s;}
.jpd .tool:hover{background:var(--navy-tile-hover);}

.jpd .board{flex:1;display:grid;grid-template-columns:repeat(${COLS},1fr);gap:8px;padding:12px;background:var(--navy-deep);}
.jpd .cat{background:var(--navy);color:var(--gold-bright);font-family:var(--heading);font-weight:700;
  display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:10px 6px;border-radius:8px;
  font-size:clamp(.8rem,1.5vw,1.2rem);line-height:1.15;text-transform:uppercase;letter-spacing:.01em;min-height:60px;}
.jpd .cat .cat-note{font-family:var(--body);text-transform:none;letter-spacing:0;color:var(--cream-dim);
  font-size:.8em;font-weight:600;margin-top:4px;}
.jpd .tile{background:var(--navy-tile);color:var(--gold);font-family:var(--heading);font-weight:700;
  font-size:clamp(1.4rem,3.2vw,2.5rem);display:flex;align-items:center;justify-content:center;border-radius:8px;
  transition:background .12s,transform .08s;text-shadow:2px 2px 0 rgba(0,0,0,.4);min-height:56px;}
.jpd .tile:hover{background:var(--navy-tile-hover);}
.jpd .tile:active{transform:scale(.97);}
.jpd .tile.used{background:var(--used);color:transparent;text-shadow:none;cursor:default;pointer-events:none;}

.jpd .scorebar{display:flex;gap:10px;padding:12px 14px;background:var(--navy);border-top:2px solid var(--gold);
  flex-wrap:wrap;justify-content:center;}
.jpd .team{background:var(--navy-tile);border-radius:12px;padding:10px 14px;min-width:150px;text-align:center;}
.jpd .team .name{font-weight:700;color:var(--cream);font-size:1rem;margin-bottom:2px;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;max-width:180px;}
.jpd .team .score{font-family:var(--heading);color:var(--gold-bright);font-size:2rem;font-weight:700;line-height:1;}
.jpd .team .adj{display:flex;gap:6px;justify-content:center;margin-top:8px;}
.jpd .team .adj button{background:var(--navy-deep);color:var(--cream);font-size:1rem;font-weight:700;width:34px;
  height:30px;border-radius:6px;transition:background .12s;}
.jpd .team .adj button:hover{background:var(--navy-tile-hover);}

.jpd .overlay{position:fixed;inset:0;background:var(--navy);display:flex;flex-direction:column;align-items:center;
  justify-content:center;padding:5vh 6vw;z-index:20;text-align:center;}
.jpd .clue-meta{color:var(--gold);font-size:1.3rem;font-weight:700;letter-spacing:.05em;margin-bottom:4vh;}
.jpd .clue-text{font-family:var(--heading);color:var(--cream);font-weight:700;
  font-size:clamp(1.8rem,5vw,4rem);line-height:1.25;max-width:20ch;animation:jpdPop .18s ease-out;}
.jpd .answer-text{font-family:var(--heading);color:var(--gold-bright);font-weight:700;
  font-size:clamp(1.4rem,3.6vw,2.8rem);line-height:1.3;margin-top:5vh;max-width:30ch;}
.jpd .clue-status{color:var(--terracotta-bright);font-size:1.05rem;font-weight:600;margin-top:3vh;
  min-height:1.4em;line-height:1.4;max-width:40ch;}
.jpd .judge-row{margin-top:3.5vh;display:flex;gap:14px;flex-wrap:wrap;justify-content:center;}
.jpd .tj{background:var(--navy-tile);border-radius:12px;padding:10px 12px 12px;display:flex;flex-direction:column;
  gap:8px;min-width:150px;border:2px solid transparent;transition:opacity .12s;}
.jpd .tj .tj-name{font-weight:700;color:var(--cream);font-size:1.05rem;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;max-width:170px;text-align:center;}
.jpd .tj .tj-btns{display:flex;gap:8px;}
.jpd .tj .tj-btns button{flex:1;font-size:1.05rem;font-weight:700;padding:12px 10px;border-radius:8px;
  transition:background .12s,transform .08s;}
.jpd .tj .tj-btns button:active{transform:scale(.96);}
.jpd .tj .correct{background:var(--green);color:#fff;}
.jpd .tj .correct:hover{background:var(--green-bright);}
.jpd .tj .wrong{background:var(--terracotta);color:#fff;}
.jpd .tj .wrong:hover{background:var(--terracotta-bright);}
.jpd .tj.got{border-color:var(--green-bright);}
.jpd .tj.out{border-color:var(--terracotta-bright);}
.jpd .tj.out .tj-name::after{content:"  \\2014 out";color:var(--terracotta-bright);font-weight:600;}
.jpd .tj .tj-btns button.on{box-shadow:inset 0 0 0 3px var(--cream);}

.jpd .clue-actions{margin-top:4vh;display:flex;gap:12px;flex-wrap:wrap;justify-content:center;}
.jpd .clue-actions button{font-size:1.15rem;font-weight:700;padding:14px 22px;border-radius:10px;
  transition:background .12s,transform .08s;}
.jpd .clue-actions button:active{transform:scale(.97);}
.jpd .reveal{background:var(--gold);color:var(--navy-deep);font-family:var(--heading);}
.jpd .reveal:hover{background:var(--gold-bright);}
.jpd .close{background:var(--navy-tile);color:var(--cream-dim);border:2px solid var(--cream-dim);}
.jpd .close:hover{background:var(--navy-tile-hover);color:var(--cream);}

.jpd .dd{position:fixed;inset:0;background:var(--navy-deep);z-index:30;display:flex;align-items:center;
  justify-content:center;flex-direction:column;cursor:pointer;}
.jpd .dd .dd-word{font-family:var(--heading);color:var(--gold);font-weight:700;
  font-size:clamp(2.5rem,10vw,7rem);text-shadow:5px 5px 0 rgba(0,0,0,.5);letter-spacing:.03em;text-align:center;
  line-height:1;animation:jpdPop .18s ease-out;}
.jpd .dd .dd-note{color:var(--cream-dim);margin-top:3vh;font-size:1.1rem;}

@keyframes jpdPop{from{transform:scale(.85);opacity:0;}to{transform:scale(1);opacity:1;}}
@media(prefers-reduced-motion:reduce){.jpd *{transition:none!important;animation:none!important;}}
@media(max-width:640px){.jpd .board{gap:5px;padding:6px;}.jpd .team{min-width:120px;}}
`;

const keyOf = (c, r) => `${c}-${r}`;

export default function HeatJeopardy() {
  const [phase, setPhase] = useState("setup");
  const [numTeams, setNumTeams] = useState(4);
  const [names, setNames] = useState(["Team 1", "Team 2", "Team 3", "Team 4"]);
  const [scores, setScores] = useState([0, 0, 0, 0]);
  const [dd, setDd] = useState([]); // keys of the two daily doubles
  const [used, setUsed] = useState([]); // keys used
  const [active, setActive] = useState(null); // {col,row,isDD,value}
  const [revealed, setRevealed] = useState(false);
  const [judged, setJudged] = useState({}); // { teamIndex: 'correct' | 'wrong' } for the open clue
  const [showDD, setShowDD] = useState(false);
  const rootRef = useRef(null);

  const activeNames = names.slice(0, numTeams);

  const clueValue = active ? (active.isDD ? active.value * 2 : active.value) : 0;

  const startGame = () => {
    setScores(Array(numTeams).fill(0));
    const picks = new Set();
    while (picks.size < 2) {
      picks.add(keyOf(Math.floor(Math.random() * COLS), Math.floor(Math.random() * ROWS)));
    }
    setDd([...picks]);
    setUsed([]);
    setPhase("game");
  };

  const openClue = (c, r) => {
    const k = keyOf(c, r);
    if (used.includes(k)) return;
    const isDD = dd.includes(k);
    setActive({ col: c, row: r, isDD, value: CATEGORIES[c].clues[r].v });
    setRevealed(false);
    setJudged({});
    if (isDD) setShowDD(true);
  };

  // Correct / Wrong are reversible toggles. They stay live before AND after the
  // answer is revealed. Clicking the same verdict again undoes it; switching a
  // team from wrong to correct (or vice-versa) reverses the old score change and
  // applies the new one, so the running total always stays right.
  const setJudge = (i, verdict) => {
    const cur = judged[i];
    let delta;
    if (cur === verdict) delta = -DELTA[verdict] * clueValue; // toggle off
    else delta = (DELTA[verdict] - (cur ? DELTA[cur] : 0)) * clueValue; // switch / set
    setScores((s) => s.map((v, idx) => (idx === i ? v + delta : v)));
    setJudged((prev) => {
      const next = { ...prev };
      if (prev[i] === verdict) delete next[i];
      else next[i] = verdict;
      return next;
    });
  };

  const revealAnswer = useCallback(() => setRevealed(true), []);

  const closeClue = useCallback(() => {
    if (!active) return;
    setUsed((u) => [...u, keyOf(active.col, active.row)]);
    setActive(null);
  }, [active]);

  const dismissDD = () => setShowDD(false);

  const adjust = (i, delta) =>
    setScores((s) => s.map((v, idx) => (idx === i ? v + delta : v)));

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const resetGame = () => {
    if (window.confirm("Start a brand new game? Current scores will be cleared.")) {
      setActive(null);
      setPhase("setup");
    }
  };

  // keyboard: Esc closes / dismisses DD; Space reveals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showDD) { setShowDD(false); return; }
        if (active) closeClue();
      }
      if (e.key === " " && active && !showDD && !revealed) {
        e.preventDefault();
        revealAnswer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, showDD, revealed, closeClue, revealAnswer]);

  const status = () => {
    const correct = activeNames.filter((_, i) => judged[i] === "correct");
    const wrong = activeNames.filter((_, i) => judged[i] === "wrong");
    if (correct.length)
      return {
        color: "var(--green-bright)",
        text: `✓ ${correct.join(", ")} ${correct.length > 1 ? "each get" : "gets"} +$${clueValue}`,
      };
    if (wrong.length >= numTeams)
      return { color: "var(--terracotta-bright)", text: "Everyone's out. Reveal the answer to move on." };
    if (wrong.length)
      return {
        color: "var(--terracotta-bright)",
        text: `✗ ${wrong.join(", ")} ${wrong.length > 1 ? "are" : "is"} out — still open, anyone else can steal!`,
      };
    if (revealed)
      return { color: "var(--cream-dim)", text: "Answer revealed." };
    return { color: "", text: "" };
  };

  return (
    <div className="jpd" ref={rootRef}>
      <style>{CSS}</style>

      {phase === "setup" && (
        <section className="setup">
          <div className="logo">
            <span className="big">JEOPARDY!</span>
            <span className="sub">HEAT &amp; THERMODYNAMICS &middot; LESSONS 21&ndash;23</span>
          </div>
          <div className="rule" />
          <div className="panel">
            <h2>How many teams?</h2>
            <div className="count">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  className={numTeams === n ? "active" : ""}
                  onClick={() => setNumTeams(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="names">
              {Array.from({ length: numTeams }).map((_, i) => (
                <input
                  key={i}
                  type="text"
                  value={names[i]}
                  placeholder={`Team ${i + 1} name`}
                  onChange={(e) =>
                    setNames((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))
                  }
                />
              ))}
            </div>
            <button className="start" onClick={startGame}>
              Start the game
            </button>
          </div>
          <p className="hint">
            Click a dollar value to show a clue. Teams buzz in with their bells or clickers &mdash; you judge who
            was first. Tap <strong>&#10003; Correct</strong> to award the value, or <strong>&#10007; Wrong</strong> to
            dock it and lock that team out; the clue stays open for anyone else to steal. Reveal the answer when it's
            settled. Two hidden <strong>Daily Doubles</strong> put double points at stake &mdash; win or lose. The
            &minus;/+ buttons under each team fix scores by hand.
          </p>
        </section>
      )}

      {phase === "game" && (
        <section className="game">
          <div className="topbar">
            <h1>Heat &amp; Thermodynamics &mdash; Lessons 21&ndash;23 Review</h1>
            <div className="tools">
              <button className="tool" onClick={toggleFullscreen}>Fullscreen</button>
              <button className="tool" onClick={resetGame}>New game</button>
            </div>
          </div>

          <div className="board">
            {CATEGORIES.map((cat, c) => (
              <div className="cat" key={`h-${c}`}>
                {cat.name}
                {cat.note && <span className="cat-note">{cat.note}</span>}
              </div>
            ))}
            {Array.from({ length: ROWS }).map((_, r) =>
              CATEGORIES.map((cat, c) => {
                const k = keyOf(c, r);
                const isUsed = used.includes(k);
                return (
                  <button
                    key={k}
                    className={`tile${isUsed ? " used" : ""}`}
                    onClick={() => openClue(c, r)}
                    disabled={isUsed}
                  >
                    ${cat.clues[r].v}
                  </button>
                );
              })
            )}
          </div>

          <div className="scorebar">
            {activeNames.map((name, i) => (
              <div className="team" key={i}>
                <div className="name">{name}</div>
                <div className="score">{scores[i]}</div>
                <div className="adj">
                  <button onClick={() => adjust(i, -100)}>&minus;</button>
                  <button onClick={() => adjust(i, 100)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {active && !showDD && (
        <div className="overlay">
          <div className="clue-meta">
            {CATEGORIES[active.col].name} &mdash; ${active.value}
            {active.isDD ? `  (Daily Double — $${clueValue} at stake!)` : ""}
          </div>
          <div className="clue-text">{CATEGORIES[active.col].clues[active.row].q}</div>
          {revealed && (
            <div className="answer-text">{CATEGORIES[active.col].clues[active.row].a}</div>
          )}
          {(() => {
            const s = status();
            return <div className="clue-status" style={{ color: s.color }}>{s.text}</div>;
          })()}

          <div className="judge-row">
            {activeNames.map((name, i) => {
              const verdict = judged[i];
              return (
                <div
                  className={`tj${verdict === "wrong" ? " out" : ""}${verdict === "correct" ? " got" : ""}`}
                  key={i}
                >
                  <div className="tj-name">{name}</div>
                  <div className="tj-btns">
                    <button
                      className={`correct${verdict === "correct" ? " on" : ""}`}
                      onClick={() => setJudge(i, "correct")}
                    >
                      &#10003; +${clueValue}
                    </button>
                    <button
                      className={`wrong${verdict === "wrong" ? " on" : ""}`}
                      onClick={() => setJudge(i, "wrong")}
                    >
                      &#10007; &minus;${clueValue}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="clue-actions">
            {!revealed && (
              <button className="reveal" onClick={revealAnswer}>Reveal answer</button>
            )}
            <button className={revealed ? "reveal" : "close"} onClick={closeClue}>
              {revealed ? "Next clue →" : "Skip — close"}
            </button>
          </div>
        </div>
      )}

      {showDD && (
        <div className="dd" onClick={dismissDD}>
          <div className="dd-word">DAILY<br />DOUBLE!</div>
          <div className="dd-note">Click anywhere to see the clue &mdash; worth double points</div>
        </div>
      )}
    </div>
  );
}